/**
 * StackStep's Gmail/Outlook + Calendar connection card: provider icon,
 * title/subtitle, the connect/retry trailing action, the "finish step one"
 * hint, and the inline connect-error banner with its dismiss action.
 */
import type { ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { CARD_BORDER } from '@/lib/base/list-card';
import { cx } from '@/utils/cx';

const CARD_CLASSES = cx(CARD_BORDER, 'bg-bg-primary');

export interface StackStepConnectionCardProps {
  icon: ReactNode;
  title: string;
  subtitle: { text: string; tone: 'muted' | 'error' } | null;
  trailing: ReactNode;
  showOrgHint: boolean;
  connectError: string | null;
  onDismissError: () => void;
}

export function StackStepConnectionCard({
  icon,
  title,
  subtitle,
  trailing,
  showOrgHint,
  connectError,
  onDismissError,
}: StackStepConnectionCardProps) {
  return (
    <div className={cx(CARD_CLASSES, 'flex flex-col gap-2 p-4')}>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {icon}
          <div className='flex flex-col'>
            <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
              {title}
            </span>
            {subtitle && (
              <span
                className={cx(
                  'text-sm',
                  subtitle.tone === 'error'
                    ? 'text-[var(--color-text-error-primary)]'
                    : 'text-[var(--color-text-tertiary)]'
                )}
              >
                {subtitle.text}
              </span>
            )}
          </div>
        </div>
        {trailing}
      </div>
      {showOrgHint && (
        <span className='text-sm text-[var(--color-text-tertiary)]'>
          Finish the first step to connect your inbox.
        </span>
      )}
      {connectError && (
        <div
          role='alert'
          className='flex items-center gap-2 text-sm text-[var(--color-text-error-primary)]'
        >
          <span>{connectError}</span>
          <Button
            size='sm'
            color='link-gray'
            onClick={onDismissError}
            noTextPadding
          >
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}
